import { useEffect, useMemo, useRef, useState } from 'react';
import { saidaEmCurso } from '../../nucleo/estilos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, Selo } from '../../componentes/base';
import { Rotulo, Selecao } from '../../componentes/formulario';
import { useToast } from '../../componentes/toast';
import { api, mensagemDeErro } from '../../nucleo/api';
import { ACENTO_PADRAO, podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { ACENTOS, type Acento, type Aparencia as DadosAparencia } from './tipos';

const PADRAO: DadosAparencia = { acento: ACENTO_PADRAO, tema_padrao: 'claro', idioma_padrao: 'pt' };

function aplicarAcento(a: Acento) {
  // Fase 2 (W32): a paleta Malgo (verde) é o padrão e vale sem o atributo.
  if (a === ACENTO_PADRAO) delete document.documentElement.dataset.acento;
  else document.documentElement.dataset.acento = a;
}

/**
 * Cor de cada acento lida do próprio CSS dos tokens (nenhum valor de cor mora aqui): troca o atributo
 * `data-acento` da raiz, lê `--acento-forte` e devolve o atributo ao que estava, tudo antes do próximo desenho.
 */
function lerCoresDosTokens(): Record<Acento, string> {
  const raiz = document.documentElement;
  const original = raiz.dataset.acento;
  const saida = {} as Record<Acento, string>;
  for (const a of ACENTOS) {
    aplicarAcento(a);
    saida[a] = getComputedStyle(raiz).getPropertyValue('--acento-forte').trim();
  }
  if (original) raiz.dataset.acento = original;
  else delete raiz.dataset.acento;
  return saida;
}

/** M13.1 Aparência: cor de marca dentro dos tokens com prévia ao vivo, tema e idioma padrão. */
export function Aparencia() {
  const { t } = useTranslation('configuracoes');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const salva = useMemo<DadosAparencia>(() => ({ ...PADRAO, ...(s.aparencia ?? {}) }), [s.aparencia]);
  const [acento, setAcento] = useState<Acento>(salva.acento);
  const [tema, setTema] = useState(salva.tema_padrao);
  const [idioma, setIdioma] = useState(salva.idioma_padrao);
  // As amostras vêm do CSS do tema ativo: ao trocar o tema da página elas são lidas de novo.
  const [versaoTema, setVersaoTema] = useState(0);
  useEffect(() => {
    const obs = new MutationObserver(() => setVersaoTema((v) => v + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-tema'] });
    return () => obs.disconnect();
  }, []);
  const cores = useMemo(lerCoresDosTokens, [versaoTema]);
  const podeEditar = podeNoWorkspace(s, 'configuracoes.editar');
  const sujo = acento !== salva.acento || tema !== salva.tema_padrao || idioma !== salva.idioma_padrao;

  // Prévia ao vivo: troca o acento da página; ao sair sem salvar, volta ao que a sessão tem.
  const acentoDaSessao = useRef(salva.acento);
  acentoDaSessao.current = salva.acento;
  useEffect(() => {
    aplicarAcento(acento);
    return () => {
      if (!saidaEmCurso()) aplicarAcento(acentoDaSessao.current);
    };
  }, [acento]);

  const salvar = useMutation({
    mutationFn: () => api('PUT', '/configuracoes/aparencia', { acento, tema_padrao: tema, idioma_padrao: idioma }),
    onSuccess: async () => {
      await s.recarregar();
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'aparencia'] });
      await qc.invalidateQueries({ queryKey: ['configuracoes', 'historico'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  return (
    <Cartao titulo={t('aparencia.titulo')}>
      <div className="flex flex-col gap-5">
        <p className="text-sm text-tinta-3">{t('aparencia.ajuda')}</p>

        <fieldset className="flex min-w-0 flex-col gap-2">
          <legend className="mb-1 text-sm font-medium text-tinta-2">{t('aparencia.acento')}</legend>
          <div role="radiogroup" aria-label={t('aparencia.acento')} className="flex flex-wrap gap-2">
            {ACENTOS.map((a) => (
              <button
                key={a}
                type="button"
                role="radio"
                aria-checked={acento === a}
                disabled={!podeEditar}
                onClick={() => setAcento(a)}
                className={`flex h-10 items-center gap-2 rounded-lg border-2 px-3 text-sm disabled:opacity-60 ${acento === a ? 'border-acento-forte bg-acento-suave text-tinta-1' : 'border-tinta-3 text-tinta-2 hover:bg-sup-2'}`}
              >
                <span className="flex size-4 items-center justify-center rounded-full" style={{ backgroundColor: cores[a] }}>
                  {acento === a && <Check className="size-3 text-sup-1" aria-hidden />}
                </span>
                {t(`aparencia.cores.${a}`)}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Rotulo id="aparencia-tema" rotulo={t('aparencia.tema')}>
            <Selecao id="aparencia-tema" disabled={!podeEditar} opcoes={(['escuro', 'claro'] as const).map((x) => ({ valor: x, rotulo: t(`aparencia.temas.${x}`) }))} value={tema} onChange={(e) => setTema(e.target.value as DadosAparencia['tema_padrao'])} />
          </Rotulo>
          <Rotulo id="aparencia-idioma" rotulo={t('aparencia.idioma')}>
            <Selecao id="aparencia-idioma" disabled={!podeEditar} opcoes={(['pt', 'en', 'es'] as const).map((x) => ({ valor: x, rotulo: t(`aparencia.idiomas.${x}`) }))} value={idioma} onChange={(e) => setIdioma(e.target.value as DadosAparencia['idioma_padrao'])} />
          </Rotulo>
        </div>

        <section aria-label={t('aparencia.previa')} className="flex min-w-0 flex-col gap-3 rounded-xl border border-borda bg-sup-2 p-4">
          <p className="kicker">{t('aparencia.previa')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 text-sm text-tinta-2">
              <span aria-hidden className="size-5 rounded-full bg-acento" />
              {t('aparencia.botao')}
            </span>
            <Selo cor="acento">{t('aparencia.destaque')}</Selo>
            <span className="border-b-2 border-acento-forte px-1 py-1.5 text-sm font-medium">{t('aparencia.aba')}</span>
          </div>
          <p className="text-xs text-tinta-3">{t('aparencia.previaTexto')}</p>
        </section>

        {podeEditar && (
          <div className="flex flex-wrap justify-end gap-2">
            <Botao
              variante="fantasma"
              disabled={!sujo}
              onClick={() => {
                setAcento(salva.acento);
                setTema(salva.tema_padrao);
                setIdioma(salva.idioma_padrao);
              }}
            >
              {t('aparencia.descartar')}
            </Botao>
            <AcaoPrimaria>
              <Botao variante="primario" disabled={!sujo} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
                {t('aparencia.salvar')}
              </Botao>
            </AcaoPrimaria>
          </div>
        )}
      </div>
    </Cartao>
  );
}
